import { useCallback, useEffect, useRef, useState } from 'react';
import { Clock, LogOut } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { SessionStatus } from '@/hooks/useAuth';

const AUTO_LOGOUT_GRACE_SECONDS = 120; // 2 minutes

interface SessionTimeoutDialogProps {
  sessionStatus: SessionStatus;
  onExtendSession: () => Promise<boolean>;
  onSignOut: () => void;
}

export function SessionTimeoutDialog({
  sessionStatus,
  onExtendSession,
  onSignOut,
}: SessionTimeoutDialogProps) {
  const { isExpiringSoon, isIdle, minutesUntilExpiry } = sessionStatus;
  const isOpen = isExpiringSoon || isIdle;
  const [secondsRemaining, setSecondsRemaining] = useState(AUTO_LOGOUT_GRACE_SECONDS);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const onSignOutRef = useRef(onSignOut);
  onSignOutRef.current = onSignOut;

  // Auto-logout countdown when dialog is open
  useEffect(() => {
    if (isOpen) {
      setSecondsRemaining(AUTO_LOGOUT_GRACE_SECONDS);
      timerRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev <= 1) {
            // Time's up — auto-logout
            onSignOutRef.current();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Dialog closed — clear timer
      setSecondsRemaining(AUTO_LOGOUT_GRACE_SECONDS);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isOpen]);

  const handleExtend = useCallback(async () => {
    // Clear timer immediately on user interaction
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    await onExtendSession();
  }, [onExtendSession]);

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
  };

  const title = isIdle ? 'Session Inactive' : 'Session Expiring';
  const description = isIdle
    ? "You've been inactive for a while. Your session will expire soon. Would you like to stay logged in?"
    : minutesUntilExpiry !== null && minutesUntilExpiry > 0
      ? `Your session will expire in ${minutesUntilExpiry} minute${minutesUntilExpiry === 1 ? '' : 's'}. Would you like to stay logged in?`
      : 'Your session is about to expire. Would you like to stay logged in?';

  // Stop activity events (mousedown, keydown, touchstart) from propagating
  // past the dialog. Without this, clicking dialog buttons triggers the
  // window-level activity listener which resets isIdle→false, closing the
  // dialog before the click handler fires.
  const stopActivityPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={() => {
        /* controlled — ignore Radix close attempts */
      }}
    >
      <AlertDialogContent
        onMouseDown={stopActivityPropagation}
        onKeyDown={stopActivityPropagation}
        onTouchStart={stopActivityPropagation}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {description}
            {isOpen && (
              <span className="mt-2 block text-sm font-medium text-destructive">
                Auto-logout in {formatCountdown(secondsRemaining)}
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Log Out
          </Button>
          <Button onClick={handleExtend}>Stay Logged In</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
